import React, { useState, useRef, useEffect } from 'react';
import { KeyboardArrowDown } from './ui/icons';
import { useTranslation } from 'react-i18next';

const flagClass = 'rounded-[2px] object-cover';

const SKFlagIcon = () => (
  <img loading="lazy" width="20" height="15" src="https://flagcdn.com/sk.svg" alt="Slovenská vlajka" className={flagClass} />
);
const ENFlagIcon = () => (
  <img loading="lazy" width="20" height="15" src="https://flagcdn.com/gb.svg" alt="English flag" className={flagClass} />
);
const PLFlagIcon = () => (
  <img loading="lazy" width="20" height="15" src="https://flagcdn.com/pl.svg" alt="Polish flag" className={flagClass} />
);
const DEFlagIcon = () => (
  <img loading="lazy" width="20" height="15" src="https://flagcdn.com/de.svg" alt="German flag" className={flagClass} />
);
const CZFlagIcon = () => (
  <img loading="lazy" width="20" height="15" src="https://flagcdn.com/cz.svg" alt="Czech flag" className={flagClass} />
);
const ROFlagIcon = () => (
  <img loading="lazy" width="20" height="15" src="https://flagcdn.com/ro.svg" alt="Romanian flag" className={flagClass} />
);
const HUFlagIcon = () => (
  <img loading="lazy" width="20" height="15" src="https://flagcdn.com/hu.svg" alt="Hungarian flag" className={flagClass} />
);

const languages = [
  { code: 'en', name: 'English', flag: ENFlagIcon },
  { code: 'sk', name: 'Slovenčina', flag: SKFlagIcon },
  { code: 'pl', name: 'Polski', flag: PLFlagIcon },
  { code: 'de', name: 'Deutsch', flag: DEFlagIcon },
  { code: 'cs', name: 'Čeština', flag: CZFlagIcon },
  { code: 'hu', name: 'Magyar', flag: HUFlagIcon },
  { code: 'ro', name: 'Română', flag: ROFlagIcon },
];

const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleClose = () => setOpen(false);

  const changeLanguage = (language: string) => {
    i18n.changeLanguage(language);
    localStorage.setItem('i18nextLng', language);
    handleClose();
  };

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        handleClose();
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const currentLanguage = i18n.language;
  const currentLang = languages.find(lang => lang.code === currentLanguage) || languages[0];
  const CurrentFlagIcon = currentLang.flag;

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen(prev => !prev)}
        className="flex items-center gap-2 px-3 py-2 rounded-xl
          border border-primary/15 dark:border-primary/20
          bg-surface/80
          backdrop-blur-[10px] transition-all duration-300
          hover:bg-primary/10 dark:hover:bg-primary/15
          hover:border-primary/30 dark:hover:border-primary/40
          hover:-translate-y-px
          hover:shadow-card-hover"
      >
        <CurrentFlagIcon />
        <span className="text-primary font-semibold text-sm tracking-[0.5px]">
          {currentLang.code.toUpperCase()}
        </span>
        <KeyboardArrowDown
          size={20}
          className={[
            'text-primary transition-transform duration-300',
            open ? 'rotate-180' : 'rotate-0',
          ].join(' ')}
        />
      </button>

      {open && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-full mt-2 min-w-[150px] z-[200]
            rounded-xl overflow-hidden
            border border-primary/15 dark:border-primary/20
            bg-surface/95
            backdrop-blur-[10px]
            shadow-pop"
        >
          {languages.map((language) => {
            const FlagIcon = language.flag;
            const isSelected = language.code === currentLanguage;
            return (
              <button
                key={language.code}
                onClick={() => changeLanguage(language.code)}
                className={[
                  'flex items-center gap-3 w-full px-4 py-3 text-left transition-colors duration-200',
                  isSelected
                    ? 'bg-primary/10 dark:bg-primary/20 border-l-[3px] border-l-primary'
                    : 'border-l-[3px] border-l-transparent hover:bg-primary/[0.08] dark:hover:bg-primary/15',
                ].join(' ')}
              >
                <FlagIcon />
                <span
                  className={[
                    'text-sm transition-colors duration-200',
                    isSelected
                      ? 'font-bold text-primary'
                      : 'font-medium text-content',
                  ].join(' ')}
                >
                  {language.name}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
